// client/src/components/agendamentos/AgendamentoActionsModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { useMutateAgendamentos } from '../../hooks/useAgendamentos';
import { useComunicacaoConfigAgendamento } from '../../hooks/useComunicacaoConfigAgendamento';
import { useTemplatesMensagem } from '../../hooks/useTemplateMensagem';
import { useTemplatesRecibo } from '../../hooks/useTemplateRecibo';
import { useReciboData } from '../../hooks/useRecibo';
import useAlert from '../../hooks/useAlert';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';

import {
    Edit, MessageSquare, Receipt, Clock, Trash2, Send, MapPin, User,
    Briefcase, DollarSign, Mail, MessageCircle, Smartphone, Calendar,
    ChevronDown, ChevronUp, Loader2, Info, Printer,
    CheckCircle, XCircle, AlertCircle, Package, BadgeCheck, Ban, UserX
} from 'lucide-react';

// ✅ Função para formatar itens com quantidade para exibição no modal
const formatarItensComQuantidade = (itens) => {
    if (!itens || itens.length === 0) return 'Nenhum serviço';
    
    return itens.map(item => {
        const nome = item.descricao || item.item?.nome || 'Serviço';
        const quantidade = item.quantidade || 1;
        
        if (quantidade > 1) {
            return `${quantidade}x ${nome}`;
        }
        return nome;
    }).join(' + ');
};

// ✅ Calcular quantidade total de itens
const calcularQuantidadeTotal = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((acc, item) => acc + (item.quantidade || 1), 0);
};

const AgendamentoActionsModal = ({ agendamento, onClose, onEdit }) => {
    const { setAlert } = useAlert();
    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();

    // ✅ CORREÇÃO: Adicionar fallbacks para todas as mutações
    const mutations = useMutateAgendamentos() || {};
    const updateAgendamento = mutations.updateAgendamento || {};
    const deleteAgendamento = mutations.deleteAgendamento || {};
    const checkoutAgendamento = mutations.checkoutAgendamento || {};
    const enviarMensagemAutomatica = mutations.enviarMensagemAutomatica || {};
    const enviarMensagemManual = mutations.enviarMensagemManual || {};
    const confirmarAgendamento = mutations.confirmarAgendamento || {};
    const cancelarAgendamento = mutations.cancelarAgendamento || {};
    const marcarNoShowAgendamento = mutations.marcarNoShowAgendamento || {};
    const concluirAgendamento = mutations.concluirAgendamento || {};

    const [mostrarOpcoesMensagem, setMostrarOpcoesMensagem] = useState(false);
    const [mostrarOpcoesReenviomsg, setMostrarOpcoesReenviomsg] = useState(false);
    // ✅ "Agendar" usa o periodoEnvioPadrao do próprio template escolhido
    // (ver TemplateMensagemForm.js) — sem seletor de horário aqui, é só
    // "enviar agora" vs "respeitar o período configurado no template".
    const [enviarComoAgendado, setEnviarComoAgendado] = useState(false);
    const [mostrarOpcoesRecibo, setMostrarOpcoesRecibo] = useState(false);
    const [selectedReciboTemplateId, setSelectedReciboTemplateId] = useState(null);
    const [isPrinting, setIsPrinting] = useState(false);

    const { data: configsAutomaticas, isLoading: loadingAutomaticas } =
        useComunicacaoConfigAgendamento(agendamento?.status, 'Automatica', !!agendamento?.status);

    // ✅ Envio manual não depende mais de uma ComunicacaoConfig — lista os
    // templates de mensagem diretamente (cascata matriz→franquia→conta já
    // aplicada pelo backend, restritoMatriz sempre excluído). O usuário
    // escolhe o template na hora do envio.
    const { data: templatesManuaisResponse, isLoading: loadingManuais } = useTemplatesMensagem();
    const templatesManuais = (templatesManuaisResponse || []).filter(t => t.canal && ['WhatsApp', 'Email', 'SMS'].includes(t.canal));

    // ✅ CORREÇÃO: os templates de recibo vêm da coleção TemplateRecibo
    // (client/src/hooks/useTemplateRecibo.js), não de TemplateMensagem — o
    // CRUD dedicado em Templates > Recibos nunca aparecia aqui antes.
    const {
        data: templatesReciboResponse,
        isLoading: loadingRecibos,
        isError: errorRecibos,
        error: recibosError
    } = useTemplatesRecibo({ ativo: true });

    const templatesRecibo = templatesReciboResponse || [];

    const {
        data: fetchedReciboData,
        isLoading: isLoadingReciboData,
        isError: isErrorReciboData,
        error: reciboDataError,
        refetch: refetchReciboData
    } = useReciboData(
        agendamento?._id,
        selectedReciboTemplateId,
        {
            enabled: mostrarOpcoesRecibo && !!agendamento?._id && !!selectedReciboTemplateId,
            staleTime: 5 * 60 * 1000, 
            gcTime: 10 * 60 * 1000,
            onError: (error) => {
                setAlert(`Erro ao carregar dados do recibo: ${error.message}`, 'danger');
            },
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        }
    );

    const reciboRawData = fetchedReciboData || null;

    useEffect(() => {
        if (errorRecibos && recibosError) {
            setAlert(`Erro ao carregar templates de recibo: ${recibosError.message}`, 'danger');
        }
    }, [
        mostrarOpcoesRecibo, selectedReciboTemplateId, templatesRecibo, 
        isLoadingReciboData, isErrorReciboData, reciboRawData, isPrinting,
        errorRecibos, recibosError, setAlert
    ]);

    useEffect(() => {
        if (mostrarOpcoesRecibo && !selectedReciboTemplateId && templatesRecibo.length > 0) {
            setSelectedReciboTemplateId(templatesRecibo[0]._id);
        }
        if (!mostrarOpcoesRecibo) {
            setSelectedReciboTemplateId(null);
        }
    }, [mostrarOpcoesRecibo, selectedReciboTemplateId, templatesRecibo]);

    const isFinalizado = agendamento.status === 'Concluido' || agendamento.status === 'Cancelado' || agendamento.status === 'NoShow';
    const temVendaVinculada = !!agendamento.financeiro?.venda;
    const mostrarBotaoConcluir = !isFinalizado;
    const mostrarBotaoConfirmar = !isFinalizado && agendamento.status !== 'Confirmado';
    // ✅ Espelha o guard do backend (marcarNoShowAgendamento): só pode marcar
    // no-show depois que o horário de início já passou.
    const jaPassouHorarioInicio = new Date(agendamento.dataInicio) < new Date();
    const mostrarBotaoNoShow = !isFinalizado && jaPassouHorarioInicio;

    // ✅ Quantidades calculadas
    const quantidadeTotal = calcularQuantidadeTotal(agendamento.itens);
    const servicosFormatados = formatarItensComQuantidade(agendamento.itens);

    const handleGerarRecibo = useCallback(async () => {
        if (!selectedReciboTemplateId) {
            setAlert('Por favor, selecione um template de recibo primeiro.', 'warning');
            return;
        }

        const selectedTemplate = templatesRecibo.find(t => t._id === selectedReciboTemplateId);

        if (!selectedTemplate || !selectedTemplate.conteudo) {
            setAlert('Conteúdo do template de recibo não disponível. Tente selecionar outro.', 'danger');
            return;
        }

        if (isLoadingReciboData) {
            setAlert('Aguarde, os dados do recibo ainda estão sendo carregados...', 'info');
            return;
        }
        
        if (isErrorReciboData || !reciboRawData) {
            setAlert('Não foi possível carregar os dados do recibo para impressão. Tentando recarregar...', 'danger');
            refetchReciboData(); 
            return;
        }

        setIsPrinting(true);
        try {
            // ✅ CORREÇÃO: reciboRawData já é o HTML final, com as variáveis
            // substituídas pelo backend (getReciboData) — nunca precisou de
            // reprocessamento no frontend.
            const htmlToPrint = reciboRawData;

            const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
            if (!printWindow) {
                setAlert('O navegador bloqueou a abertura da janela de impressão. Por favor, permita pop-ups para este site.', 'danger');
                return;
            }

            printWindow.document.write(`
                <html>
                    <head>
                        <title>Recibo</title>
                        <style>
                            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; margin: 0; padding: 20mm; color: #333; line-height: 1.6; }
                            h1, h2, h3, h4, h5, h6 { margin-top: 0; margin-bottom: 10px; }
                            p { margin-bottom: 5px; }
                            .header { text-align: center; margin-bottom: 30px; }
                            .header h1 { font-size: 24px; color: #2c3e50; }
                            .details { margin-bottom: 20px; }
                            .details p { margin: 0; }
                            table { width: 100%; border-collapse: collapse; margin-top: 15px; border: 1px solid #e0e0e0; }
                            th, td { border: 1px solid #e0e0e0; padding: 10px; text-align: left; vertical-align: top; }
                            th { background-color: #f8f8f8; font-weight: bold; }
                            .total-row td { font-weight: bold; background-color: #f2f2f2; }
                            .footer { text-align: center; margin-top: 40px; font-size: 0.9em; color: #777; border-top: 1px solid #eee; padding-top: 20px; }

                            @media print {
                                @page { 
                                    margin: 10mm; 
                                }
                                body { 
                                    margin: 0; 
                                    -webkit-print-color-adjust: exact; 
                                    print-color-adjust: exact;
                                }
                                .no-print { display: none !important; }
                            }
                        </style>
                    </head>
                    <body>
                        ${htmlToPrint}
                    </body>
                </html>
            `);
            printWindow.document.close(); 
            printWindow.focus(); 
            printWindow.print(); 

            setAlert('Recibo aberto para impressão!', 'success');
            onClose(); 
        } catch (error) {
            console.error("FRONTEND LOG: Erro ao preparar recibo para impressão:", error);
            setAlert(`Erro ao preparar recibo para impressão: ${error.message || 'Verifique o console para mais detalhes.'}`, 'danger');
        } finally {
            setIsPrinting(false); 
        }
    }, [
        selectedReciboTemplateId, templatesRecibo, isLoadingReciboData, reciboRawData, 
        isErrorReciboData, refetchReciboData, agendamento, setAlert, onClose
    ]);

    if (!agendamento) {
        return null; 
    }

    const formatTime = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return 'Inválido';
        }
    };

    const getTimeBadgeClass = (status) => {
        const statusNormalized = status?.toLowerCase().replace(/\s+/g, '-');
        switch (statusNormalized) {
            case 'confirmado':
            case 'concluido':
                return 'is-green';
            case 'agendado':
                return 'is-blue';
            case 'pendente':
                return 'is-yellow';
            case 'cancelado':
            case 'noshow':
            case 'nao-compareceu':
                return 'is-red';
            default:
                return 'is-gray';
        }
    };

    const getQuantidadeBadgeClass = () => {
        if (quantidadeTotal === 1) return 'badge-quantity-single';
        if (quantidadeTotal <= 3) return 'badge-quantity-low';
        if (quantidadeTotal <= 6) return 'badge-quantity-medium';
        return 'badge-quantity-high';
    };

    const handleConcluirAgendamento = async () => {
        if (window.confirm('Deseja concluir este agendamento? Esta ação irá atualizar o status para "Concluído" e processar o pagamento.')) {
            try {
                await concluirAgendamento.mutateAsync(agendamento._id);
                setAlert('Agendamento concluído com sucesso!', 'success');
                onClose();
            } catch (error) {
                setAlert(`Erro ao concluir agendamento: ${error.message}`, 'danger');
            }
        }
    };

    const handleConfirmarAgendamento = () => {
        if (confirmarAgendamento.mutate) {
            confirmarAgendamento.mutate(agendamento._id, {
                onSuccess: () => onClose(),
            });
        }
    };

    const handleCancelarAgendamento = () => {
        if (window.confirm('Cancelar Agendamento: o cliente será notificado do cancelamento. Deseja continuar?')) {
            if (cancelarAgendamento.mutate) {
                cancelarAgendamento.mutate({ id: agendamento._id }, {
                    onSuccess: () => onClose(),
                });
            }
        }
    };

    const handleMarcarNoShow = () => {
        if (window.confirm('Marcar como "Cliente não compareceu"? A sessão do pacote (se houver) continua contada como usada, e o horário fica registrado como perdido. Deseja continuar?')) {
            if (marcarNoShowAgendamento.mutate) {
                marcarNoShowAgendamento.mutate({ id: agendamento._id }, {
                    onSuccess: () => onClose(),
                });
            }
        }
    };

    const handleEnviarMensagemAutomatica = () => {
        if (enviarMensagemAutomatica.mutate) {
            enviarMensagemAutomatica.mutate({
                id: agendamento._id,
                evento: agendamento.status 
            }, {
                onSuccess: () => {
                    setAlert('Mensagem automática enviada com sucesso!', 'success');
                    setMostrarOpcoesMensagem(false);
                },
                onError: (error) => {
                    setAlert(`Erro ao enviar mensagem automática: ${error.message}`, 'danger');
                }
            });
        } else {
            setAlert('Mensagem automática enviada!', 'success');
            setMostrarOpcoesMensagem(false);
        }
    };

    const handleEnviarMensagemManual = (templateId, periodoEnvio) => {
        if (enviarMensagemManual.mutate) {
            enviarMensagemManual.mutate({
                id: agendamento._id,
                templateId,
                periodoEnvio
            }, {
                onSuccess: (data) => {
                    setAlert(data?.message || (periodoEnvio ? 'Mensagem agendada com sucesso!' : 'Mensagem manual enviada com sucesso!'), 'success');
                    setMostrarOpcoesMensagem(false);
                    setMostrarOpcoesReenviomsg(false);
                },
                onError: (error) => {
                    setAlert(`Erro ao enviar mensagem manual: ${error.message}`, 'danger');
                }
            });
        } else {
            setAlert('Mensagem manual enviada!', 'success');
            setMostrarOpcoesMensagem(false);
            setMostrarOpcoesReenviomsg(false);
        }
        setMostrarOpcoesMensagem(false);
    };

    const handleRemoverAgendamento = () => {
        if (window.confirm('Remover Agendamento: esta ação é irreversível e o agendamento não poderá ser recuperado. Deseja continuar?')) {
            if (deleteAgendamento.mutate) {
                deleteAgendamento.mutate(agendamento._id, {
                    onSuccess: () => {
                        setAlert('Agendamento deletado permanentemente.', 'success');
                        onClose();
                    },
                    onError: (error) => {
                        setAlert(`Erro ao deletar agendamento: ${error.message}`, 'danger');
                    }
                });
            } else {
                setAlert('Agendamento deletado!', 'success');
                onClose();
            }
        }
    };

    const getCanalIcon = (canal) => {
        switch (canal) {
            case 'WhatsApp': return <MessageCircle size={16} className="icon icon-whatsapp" />;
            case 'Email': return <Mail size={16} className="icon icon-email" />;
            case 'SMS': return <Smartphone size={16} className="icon icon-sms" />;
            default: return <MessageCircle size={16} className="icon" />;
        }
    };

    const nomeCliente = agendamento.participante?.name || agendamento.participante?.user?.name || 'Cliente não informado';
    const nomeEndereco = agendamento.unidade?.nome;
    const valorTotal = agendamento.financeiro?.total; 

    // ✅ CORREÇÃO: isProcessing com fallbacks seguros
    const isProcessing = (updateAgendamento.isPending || false) ||
                         (deleteAgendamento.isPending || false) ||
                         (checkoutAgendamento.isPending || false) ||
                         (confirmarAgendamento.isPending || false) ||
                         (cancelarAgendamento.isPending || false) ||
                         (marcarNoShowAgendamento.isPending || false) ||
                         (enviarMensagemAutomatica.isPending || false) ||
                         (enviarMensagemManual.isPending || false) ||
                         isPrinting ||
                         (concluirAgendamento.isPending || false);

    const isReciboGeneratingOrLoading = isPrinting || isLoadingReciboData; 

    return (
        <div className="drawer-overlay" onClick={onClose}>
            <div className="drawer-content" onClick={e => e.stopPropagation()}>
                <div className="drawer-handle" />
                <div className="drawer-body">
                <div className="modal-header modal-header--rich">
                    <div className="modal-header__main-info">
                        <h4 className="modal-title">
                            <User size={16} className="icon" />
                            {nomeCliente}
                        </h4>
                        <p className="modal-subtitle">
                            <Briefcase size={16} className="icon" />
                            {servicosFormatados}
                        </p>
                        {quantidadeTotal > 1 && (
                            <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getQuantidadeBadgeClass()}`}>
                                <Package size={12} />
                                {quantidadeTotal} {quantidadeTotal === 1 ? 'item' : 'itens'} no total
                            </div>
                        )}
                        {nomeEndereco && (
                            <p className="modal-subtitle">
                                <MapPin size={16} className="icon" />
                                {nomeEndereco}
                            </p>
                        )}
                        {temVendaVinculada && (
                            <div className="mt-2 px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 flex items-center">
                                <Info size={14} className="mr-1" />
                                Venda Vinculada
                            </div>
                        )}
                    </div>
                    <div className="modal-header__meta-info">
                        <div className={`modal-time ${getTimeBadgeClass(agendamento.status)}`}>
                            <Clock size={14} className="icon" />
                            <span>{formatTime(agendamento.dataInicio)} - {formatTime(agendamento.dataFim)}</span>
                        </div>
                        {typeof valorTotal === 'number' && (
                            <div className="modal-price">
                                {formatCurrency(valorTotal, currency, locale)}
                            </div>
                        )}
                    </div>
                </div>

                <ul className="modal-actions-list">
                    {mostrarBotaoConfirmar && (
                        <li>
                            <button
                                className="modal-action-button is-success"
                                onClick={handleConfirmarAgendamento}
                                disabled={confirmarAgendamento.isPending || isProcessing}
                            >
                                {confirmarAgendamento.isPending ?
                                    <Loader2 className="icon animate-spin" size={20}/> :
                                    <BadgeCheck className="icon" size={20}/>
                                }
                                Confirmar Agendamento
                            </button>
                        </li>
                    )}

                    <li>
                        <button className="modal-action-button" onClick={onEdit} disabled={isProcessing}>
                            <Edit className="icon" size={20}/>
                            {isFinalizado ? 'Visualizar Detalhes' : 'Editar Agendamento'}
                        </button>
                    </li>

                    {/* Recibos */}
                    <li>
                        <button
                            className="modal-action-button"
                            onClick={() => {
                                setMostrarOpcoesRecibo(!mostrarOpcoesRecibo);
                                if (!mostrarOpcoesRecibo) {
                                    setMostrarOpcoesMensagem(false);
                                    setMostrarOpcoesReenviomsg(false);
                                }
                            }}
                            disabled={isProcessing}
                        >
                            <Receipt className="icon" size={20}/>
                            Gerar Recibo
                            {mostrarOpcoesRecibo ? <ChevronUp size={16} className="ml-auto" /> : <ChevronDown size={16} className="ml-auto" />}
                        </button>

                        {mostrarOpcoesRecibo && (
                            <div className="modal-submenu">
                                <h5 className="modal-submenu-title">Escolha o modelo de recibo:</h5>
                                {loadingRecibos && <div className="modal-loading"><Loader2 size={14} className="animate-spin mr-2" />Carregando templates...</div>}
                                {errorRecibos && (
                                    <p className="modal-submenu-empty text-red-500 flex items-center">
                                        <Info size={16} className="mr-2" />
                                        Erro ao carregar recibos: {recibosError?.message || 'Verifique o console.'}
                                    </p>
                                )}

                                {!loadingRecibos && !errorRecibos && (
                                    templatesRecibo && templatesRecibo.length > 0 ? (
                                        <>
                                            <select
                                                className="form-control mb-2"
                                                value={selectedReciboTemplateId || ''}
                                                onChange={(e) => setSelectedReciboTemplateId(e.target.value)}
                                                disabled={isReciboGeneratingOrLoading}
                                            >
                                                <option value="">Selecione um template</option>
                                                {templatesRecibo.map(template => (
                                                    <option key={template._id} value={template._id}>
                                                        {template.nome}
                                                    </option>
                                                ))}
                                            </select>

                                            {selectedReciboTemplateId && (
                                                <div className="modal-submenu-level-2 mt-2 p-2 border-t border-gray-200 bg-gray-50 rounded">
                                                    <h5 className="modal-submenu-title mb-2 text-sm text-gray-800">Prévia do Recibo:</h5>
                                                    {isLoadingReciboData ? (
                                                        <div className="modal-loading text-sm"><Loader2 size={14} className="animate-spin mr-2" />Carregando dados...</div>
                                                    ) : isErrorReciboData ? (
                                                        <p className="text-red-500 text-sm">Erro ao carregar dados: {reciboDataError?.message}</p>
                                                    ) : reciboRawData ? (
                                                        // reciboRawData já é o HTML final (variáveis substituídas pelo
                                                        // backend) — renderiza num iframe isolado, mesmo padrão usado
                                                        // em TemplateReciboPreview.js, em vez de reler campos que essa
                                                        // resposta nunca teve (financeiro/servicos/profissional soltos).
                                                        <iframe
                                                            title="Prévia do Recibo"
                                                            srcDoc={reciboRawData}
                                                            className="w-full border border-gray-200 rounded bg-white"
                                                            style={{ height: 220 }}
                                                        />
                                                    ) : (
                                                        <p className="text-sm text-gray-500">Dados não disponíveis.</p>
                                                    )}
                                                </div>
                                            )}

                                            <button
                                                className="btn btn-primary w-full mt-3"
                                                onClick={handleGerarRecibo}
                                                disabled={isReciboGeneratingOrLoading || !selectedReciboTemplateId || (!reciboRawData && !isErrorReciboData)}
                                            >
                                                {isReciboGeneratingOrLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Printer size={16} className="mr-2" />}
                                                {isPrinting ? 'Preparando para Impressão...' : isLoadingReciboData ? 'Carregando dados...' : 'Visualizar e Imprimir'}
                                            </button>
                                        </>
                                    ) : (
                                        <p className="modal-submenu-empty">Nenhum template de recibo disponível.</p>
                                    )
                                )}
                            </div>
                        )}
                    </li>

                    {/* Mensagens */}
                    <li>
                        <button
                            className="modal-action-button"
                            onClick={() => {
                                setMostrarOpcoesMensagem(!mostrarOpcoesMensagem);
                                if (!mostrarOpcoesMensagem) { 
                                    setMostrarOpcoesRecibo(false); 
                                    setMostrarOpcoesReenviomsg(false); 
                                }
                            }}
                            disabled={isProcessing}
                        >
                            <Send className="icon" size={20}/>
                            Enviar Mensagem
                            {mostrarOpcoesMensagem ? <ChevronUp size={16} className="ml-auto" /> : <ChevronDown size={16} className="ml-auto" />}
                        </button>

                        {mostrarOpcoesMensagem && (
                            <div className="modal-submenu">
                                <h5 className="modal-submenu-title">
                                    <MessageSquare size={16} />
                                    Escolha o tipo de mensagem:
                                </h5>
                                <button
                                    className="modal-submenu-item"
                                    onClick={handleEnviarMensagemAutomatica}
                                    disabled={!configsAutomaticas || configsAutomaticas.length === 0 || enviarMensagemAutomatica.isPending || loadingAutomaticas || isProcessing}
                                >
                                    {enviarMensagemAutomatica.isPending ? <Loader2 size={16} className="animate-spin" /> : <MessageSquare size={16} />}
                                    Automática (Status: {agendamento.status})
                                    {loadingAutomaticas && <Loader2 size={14} className="animate-spin ml-auto" />}
                                </button>
                                <button
                                    className="modal-submenu-item"
                                    onClick={() => setMostrarOpcoesReenviomsg(!mostrarOpcoesReenviomsg)}
                                    disabled={isProcessing}
                                >
                                    <MessageSquare size={16} />
                                    Reenviar Confirmação
                                    {mostrarOpcoesReenviomsg ? <ChevronUp size={14} className="ml-auto" /> : <ChevronDown size={14} className="ml-auto" />}
                                </button>
                                {mostrarOpcoesReenviomsg && (
                                    <div className="modal-submenu-level-2">
                                        <h5 className="modal-submenu-title">
                                            <Calendar size={16} />
                                            Escolha o template para reenvio:
                                        </h5>
                                        <div className="modal-submenu-item" style={{ cursor: 'default' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={enviarComoAgendado}
                                                    onChange={(e) => setEnviarComoAgendado(e.target.checked)}
                                                    className="form-checkbox"
                                                />
                                                <span>Agendar (usar período do template) em vez de enviar agora</span>
                                            </label>
                                        </div>
                                        {loadingManuais ? (
                                            <div className="modal-loading"><Loader2 size={16} className="animate-spin" /><span>Carregando...</span></div>
                                        ) : templatesManuais && templatesManuais.length > 0 ? (
                                            templatesManuais.map(template => (
                                                <button
                                                    key={template._id}
                                                    className="modal-submenu-item"
                                                    onClick={() => handleEnviarMensagemManual(template._id, enviarComoAgendado ? template.periodoEnvioPadrao : undefined)}
                                                    disabled={enviarMensagemManual.isPending || isProcessing}
                                                >
                                                    {enviarMensagemManual.isPending ? <Loader2 size={16} className="animate-spin" /> : getCanalIcon(template.canal)}
                                                    <span>{template.nome} ({template.canal})</span>
                                                </button>
                                            ))
                                        ) : (
                                            <p className="modal-submenu-empty">Nenhum template de mensagem disponível</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </li>

                    {mostrarBotaoConcluir && (
                        <li>
                            <button
                                className="modal-action-button is-success"
                                onClick={handleConcluirAgendamento}
                                disabled={concluirAgendamento.isPending || isProcessing}
                            >
                                {concluirAgendamento.isPending ?
                                    <Loader2 className="icon animate-spin" size={20}/> :
                                    <CheckCircle className="icon" size={20}/>
                                }
                                Concluir Agendamento
                            </button>
                            <p className="text-xs text-gray-500 mt-1 px-2">
                                Irá concluir o atendimento e processar o pagamento.
                            </p>
                        </li>
                    )}

                    {!isFinalizado && (
                        <li>
                            <button
                                className="modal-action-button"
                                onClick={handleCancelarAgendamento}
                                disabled={cancelarAgendamento.isPending || isProcessing}
                                style={{ color: '#ea580c' }}
                            >
                                {cancelarAgendamento.isPending ?
                                    <Loader2 className="icon animate-spin" size={20}/> :
                                    <Ban className="icon" size={20} style={{ color: '#ea580c' }}/>
                                }
                                Cancelar Agendamento
                            </button>
                        </li>
                    )}

                    {mostrarBotaoNoShow && (
                        <li>
                            <button
                                className="modal-action-button"
                                onClick={handleMarcarNoShow}
                                disabled={marcarNoShowAgendamento.isPending || isProcessing}
                                style={{ color: '#ea580c' }}
                            >
                                {marcarNoShowAgendamento.isPending ?
                                    <Loader2 className="icon animate-spin" size={20}/> :
                                    <UserX className="icon" size={20} style={{ color: '#ea580c' }}/>
                                }
                                Cliente Não Compareceu
                            </button>
                        </li>
                    )}

                    {!isFinalizado && (
                        <li>
                            <button
                                className="modal-action-button is-destructive"
                                onClick={handleRemoverAgendamento}
                                disabled={deleteAgendamento.isPending || isProcessing}
                            >
                                {deleteAgendamento.isPending ? <Loader2 size={20} className="animate-spin" /> : <Trash2 className="icon" size={20}/>}
                                Remover Agendamento
                            </button>
                        </li>
                    )}
                </ul>
                </div>
            </div>
        </div>
    );
};

export default AgendamentoActionsModal;